import React, { useEffect, useState, useMemo } from 'react';
import { Cake, Mail, Check, Eye, Send, Search, ChevronUp, ChevronDown, User, Calendar, CreditCard, Clock, Filter, Trash2, BellRing } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { useAuth } from '../context/AuthContext';
import api from '../lib/api';
import { formatDate } from '../lib/utils';
import { toast } from 'sonner';

export const Reminders = () => {
  const { user } = useAuth();
  const [allReminders, setAllReminders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all'); // all, birthday, renewal, payment
  const [filterStatus, setFilterStatus] = useState('all'); // all, pending, sent
  const [filterRead, setFilterRead] = useState('all'); // all, read, unread
  const [sortConfig, setSortConfig] = useState({ key: 'due_date', direction: 'asc' });
  
  // Estados para preview y envío
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [sendingId, setSendingId] = useState(null);

  useEffect(() => {
    fetchReminders();
  }, []);

  const calculateAge = (birthDate) => {
    if (!birthDate) return null;
    const today = new Date();
    const birth = new Date(birthDate);
    const currentYear = today.getFullYear();
    let age = currentYear - birth.getFullYear();
    return age;
  };

  const fetchReminders = async () => {
    try {
      setLoading(true);
      // Primero, limpiar automáticamente duplicados y huérfanos (solo para admins)
      if (user?.role === 'admin') {
        try {
            await api.post('/reminders/cleanup-orphans');
        } catch (e) {
            console.error("Error cleaning orphans", e);
        }
      }
      
      const response = await api.get('/reminders');
      
      // Procesar datos adicionales según tipo
      const processedReminders = response.data.map(reminder => {
        let extraData = {};
        
        if (reminder.type === 'birthday') {
            extraData.age = calculateAge(reminder.due_date);
        }
        
        return {
          ...reminder,
          ...extraData,
          dueDateObj: new Date(reminder.due_date)
        };
      });
      
      setAllReminders(processedReminders);
    } catch (error) {
      toast.error('Error al cargar los recordatorios');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleMarkAsRead = async (reminderId) => {
    try {
      await api.put(`/reminders/${reminderId}/read`);
      toast.success('Recordatorio marcado como leído');
      // Actualizar estado local
      setAllReminders(prev => prev.map(r => r.id === reminderId ? { ...r, is_read: true } : r));
    } catch (error) {
      toast.error('Error al actualizar el recordatorio');
    }
  };

  const handleMarkAsUnread = async (reminderId) => {
    try {
      await api.put(`/reminders/${reminderId}/unread`);
      toast.success('Recordatorio marcado como no leído');
      // Actualizar estado local
      setAllReminders(prev => prev.map(r => r.id === reminderId ? { ...r, is_read: false } : r));
    } catch (error) {
      toast.error('Error al actualizar el recordatorio');
    }
  };

  const handlePreview = async (reminderId) => {
    setLoadingPreview(true);
    setPreviewOpen(true);
    try {
      const response = await api.get(`/reminders/${reminderId}/preview`);
      setPreviewData(response.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al cargar preview');
      setPreviewOpen(false);
    } finally {
      setLoadingPreview(false);
    }
  };

  const handleSendIndividual = async (reminderId) => {
    if (!window.confirm('¿Deseas enviar este recordatorio por email?')) {
      return;
    }

    setSendingId(reminderId);
    try {
      const response = await api.post(`/reminders/${reminderId}/send`);
      toast.success(response.data.message || 'Email enviado exitosamente');
      
      setAllReminders(prev => prev.map(r => 
        r.id === reminderId 
          ? { ...r, email_sent: true, email_sent_at: new Date().toISOString() }
          : r
      ));
      
      setPreviewOpen(false);
    } catch (error) {
      const detail = error.response?.data?.detail;
      if (typeof detail === 'string') {
        toast.error(detail);
      } else {
        toast.error('Error al enviar recordatorio');
      }
    } finally {
      setSendingId(null);
    }
  };

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const getTypeConfig = (type) => {
      switch (type) {
          case 'birthday':
              return { label: 'Cumpleaños', icon: Cake, color: 'text-pink-600 bg-pink-100' };
          case 'payment':
              return { label: 'Pago', icon: CreditCard, color: 'text-blue-600 bg-blue-100' };
          case 'renewal':
              return { label: 'Renovación', icon: Clock, color: 'text-amber-600 bg-amber-100' };
          default:
              return { label: type, icon: BellRing, color: 'text-slate-600 bg-slate-100' };
      }
  };

  // Filtrado y ordenamiento
  const filteredAndSortedReminders = useMemo(() => {
    let filtered = allReminders.filter(reminder => {
      // Filtrar por tipo
      if (filterType !== 'all' && reminder.type !== filterType) return false;
      
      // Filtrar por búsqueda
      if (searchTerm) {
        const searchLower = searchTerm.toLowerCase();
        const messageMatch = reminder.message?.toLowerCase().includes(searchLower);
        if (!messageMatch) return false;
      }
      
      // Filtrar por estado de lectura
      if (filterRead === 'read' && !reminder.is_read) return false;
      if (filterRead === 'unread' && reminder.is_read) return false;
      
      // Filtrar por email enviado
      if (filterStatus === 'sent' && !reminder.email_sent) return false;
      if (filterStatus === 'pending' && reminder.email_sent) return false;
      
      return true;
    });
    
    // Ordenar
    filtered.sort((a, b) => {
      let aVal, bVal;
      
      if (sortConfig.key === 'due_date') {
        aVal = a.dueDateObj.getTime();
        bVal = b.dueDateObj.getTime();
      } else if (sortConfig.key === 'message') {
        aVal = a.message || '';
        bVal = b.message || '';
      } else if (sortConfig.key === 'type') {
        aVal = a.type || '';
        bVal = b.type || '';
      } else {
        aVal = a[sortConfig.key];
        bVal = b[sortConfig.key];
      }
      
      if (aVal === bVal) return 0;
      const comparison = aVal < bVal ? -1 : 1;
      return sortConfig.direction === 'asc' ? comparison : -comparison;
    });
    
    return filtered;
  }, [allReminders, searchTerm, filterType, filterStatus, filterRead, sortConfig]);

  // Calcular estadísticas
  const stats = useMemo(() => {
    const total = filteredAndSortedReminders.length;
    const pending = filteredAndSortedReminders.filter(r => !r.email_sent).length;
    const sent = filteredAndSortedReminders.filter(r => r.email_sent).length;
    
    return { total, pending, sent };
  }, [filteredAndSortedReminders]);

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-slate-600">Cargando recordatorios...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-[1400px] mx-auto">
      {/* Header */}
      <div className="mb-6">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
          <div>
            <h1 className="text-3xl font-bold text-slate-900">Recordatorios Pendientes</h1>
            <p className="text-slate-600">Gestiona tus tareas diarias: cumpleaños, pagos y renovaciones</p>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-slate-600">
                Total Recordatorios
              </CardTitle>
              <Filter className="h-4 w-4 text-slate-400" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-slate-900">{stats.total}</div>
            </CardContent>
          </Card>
          
          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-slate-600">
                Pendientes de Enviar
              </CardTitle>
              <Send className="h-4 w-4 text-amber-500" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-amber-600">{stats.pending}</div>
            </CardContent>
          </Card>
          
          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-slate-600">
                Emails Enviados
              </CardTitle>
              <Check className="h-4 w-4 text-green-500" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-green-600">{stats.sent}</div>
            </CardContent>
          </Card>
        </div>

        {/* Filtros y Búsqueda */}
        <div className="flex flex-col md:flex-row gap-3 items-center bg-white p-4 rounded-lg border border-gray-200 shadow-sm">
          <div className="relative flex-1 w-full md:w-auto">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 h-4 w-4" />
            <Input
              placeholder="Buscar por mensaje..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
          
          <Select value={filterType} onValueChange={setFilterType}>
            <SelectTrigger className="w-full md:w-[180px]">
              <SelectValue placeholder="Tipo" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos los tipos</SelectItem>
              <SelectItem value="birthday">Cumpleaños</SelectItem>
              <SelectItem value="renewal">Renovación</SelectItem>
              <SelectItem value="payment">Pago</SelectItem>
            </SelectContent>
          </Select>

          <Select value={filterStatus} onValueChange={setFilterStatus}>
            <SelectTrigger className="w-full md:w-[180px]">
              <SelectValue placeholder="Estado de envío" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos</SelectItem>
              <SelectItem value="pending">Pendientes</SelectItem>
              <SelectItem value="sent">Enviados</SelectItem>
            </SelectContent>
          </Select>

          <Select value={filterRead} onValueChange={setFilterRead}>
            <SelectTrigger className="w-full md:w-[180px]">
              <SelectValue placeholder="Leídos" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos</SelectItem>
              <SelectItem value="unread">No leídos</SelectItem>
              <SelectItem value="read">Leídos</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Tabla */}
      <Card>
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50">
                <TableHead onClick={() => handleSort('type')} className="cursor-pointer hover:bg-slate-100 w-[50px]">
                    <div className="flex items-center gap-1">Tipo {sortConfig.key === 'type' && (sortConfig.direction === 'asc' ? <ChevronUp size={14} /> : <ChevronDown size={14} />)}</div>
                </TableHead>
                <TableHead onClick={() => handleSort('message')} className="cursor-pointer hover:bg-slate-100">
                    <div className="flex items-center gap-1">Mensaje {sortConfig.key === 'message' && (sortConfig.direction === 'asc' ? <ChevronUp size={14} /> : <ChevronDown size={14} />)}</div>
                </TableHead>
                <TableHead onClick={() => handleSort('due_date')} className="cursor-pointer hover:bg-slate-100">
                    <div className="flex items-center gap-1">Fecha {sortConfig.key === 'due_date' && (sortConfig.direction === 'asc' ? <ChevronUp size={14} /> : <ChevronDown size={14} />)}</div>
                </TableHead>
                <TableHead>Estado</TableHead>
                <TableHead>Email</TableHead>
                <TableHead className="text-right">Acciones</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredAndSortedReminders.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-12 text-slate-500">
                    <div className="flex flex-col items-center">
                        <BellRing className="h-12 w-12 text-slate-300 mb-2" />
                        <p className="text-lg font-medium">No hay recordatorios pendientes</p>
                        <p className="text-sm">Todo está al día.</p>
                    </div>
                  </TableCell>
                </TableRow>
              ) : (
                filteredAndSortedReminders.map((reminder) => {
                  const typeConfig = getTypeConfig(reminder.type);
                  const Icon = typeConfig.icon;
                  
                  return (
                    <TableRow 
                      key={reminder.id}
                      className={`transition-colors ${reminder.is_read ? 'bg-white' : 'bg-blue-50/30'}`}
                    >
                      <TableCell>
                        <div className={`p-2 rounded-full inline-flex ${typeConfig.color}`} title={typeConfig.label}>
                            <Icon size={18} />
                        </div>
                      </TableCell>
                      <TableCell className="font-medium">
                        <div className="flex flex-col">
                            <span className="text-slate-900">{reminder.message}</span>
                            {reminder.type === 'birthday' && reminder.age && (
                                <span className="text-xs text-slate-500">Cumple {reminder.age} años</span>
                            )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center text-sm text-slate-600">
                          <Calendar className="mr-2 h-4 w-4 text-slate-400" />
                          {formatDate(reminder.due_date)}
                        </div>
                      </TableCell>
                      <TableCell>
                        {reminder.is_read ? (
                          <Badge variant="outline" className="text-slate-500 font-normal">Leído</Badge>
                        ) : (
                          <Badge className="bg-blue-100 text-blue-700 hover:bg-blue-200 border-0 font-medium">Nuevo</Badge>
                        )}
                      </TableCell>
                      <TableCell>
                        {reminder.email_sent ? (
                          <Badge className="bg-green-100 text-green-700 hover:bg-green-200 border-0">
                            <Check className="h-3 w-3 mr-1" /> Enviado
                          </Badge>
                        ) : (
                          <Badge variant="outline" className="text-amber-600 border-amber-200 bg-amber-50">
                            Pendiente
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-2">
                          {!reminder.email_sent && (
                            <Button
                              size="sm"
                              onClick={() => handleSendIndividual(reminder.id)}
                              disabled={sendingId === reminder.id}
                              className="bg-[#2537b0] hover:bg-[#1e2b8f] text-white shadow-sm h-8"
                            >
                              <Send className="h-3.5 w-3.5 mr-1.5" />
                              {sendingId === reminder.id ? '...' : 'Enviar Email'}
                            </Button>
                          )}
                          
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handlePreview(reminder.id)}
                            className="text-slate-500 hover:text-[#2537b0] h-8 w-8 p-0"
                            title="Ver detalle"
                          >
                            <Eye className="h-4 w-4" />
                          </Button>

                          {!reminder.is_read ? (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleMarkAsRead(reminder.id)}
                              className="text-slate-500 hover:text-green-600 h-8 w-8 p-0"
                              title="Marcar como leído"
                            >
                              <Check className="h-4 w-4" />
                            </Button>
                          ) : (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleMarkAsUnread(reminder.id)}
                              className="text-slate-400 hover:text-slate-600 h-8 w-8 p-0"
                              title="Marcar como no leído"
                            >
                              <Check className="h-4 w-4 opacity-50" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </div>
      </Card>

      {/* Preview Dialog - Email Renderizado */}
      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold">Vista Previa del Email</DialogTitle>
          </DialogHeader>
          
          <div className="flex-1 overflow-y-auto pr-2">
            {loadingPreview ? (
                <div className="flex justify-center items-center py-12">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
                    <p className="text-slate-600">Cargando preview...</p>
                </div>
                </div>
            ) : previewData ? (
                <div className="space-y-4">
                {/* Info del email */}
                <div className="bg-slate-50 rounded-lg p-4 space-y-2 border border-slate-200 text-sm">
                    <div className="grid grid-cols-[60px_1fr] gap-2 items-center">
                        <span className="font-semibold text-slate-500 uppercase text-xs">Para:</span>
                        <span className="font-medium text-slate-900">{previewData.to_email}</span>
                    </div>
                    <div className="grid grid-cols-[60px_1fr] gap-2 items-center">
                        <span className="font-semibold text-slate-500 uppercase text-xs">Asunto:</span>
                        <span className="text-slate-900">{previewData.subject}</span>
                    </div>
                </div>
                
                {/* Preview del email en iframe */}
                <div>
                    <div className="border border-slate-200 rounded-lg overflow-hidden bg-white shadow-sm">
                    <iframe
                        title="Email Preview"
                        srcDoc={previewData.html_content}
                        className="w-full bg-white"
                        style={{ height: '500px', border: 'none' }}
                        sandbox="allow-same-origin"
                    />
                    </div>
                </div>
                </div>
            ) : null}
          </div>
          
          <DialogFooter className="mt-4 pt-4 border-t">
            <Button
              variant="outline"
              onClick={() => setPreviewOpen(false)}
            >
              Cerrar
            </Button>
            {previewData && !previewData.reminder?.email_sent && (
              <Button
                onClick={() => handleSendIndividual(previewData.reminder.id)}
                disabled={sendingId === previewData.reminder.id}
                className="bg-[#2537b0] hover:bg-[#1e2b8f]"
              >
                <Send size={16} className="mr-2" />
                {sendingId === previewData.reminder.id ? 'Enviando...' : 'Enviar Email Ahora'}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
